import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import axios from 'axios';
import config from '../config';
import './aproposApp.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Compare deux versions « 1.29.0 » → <0, 0, >0 (tolérant, miroir de MajApp).
function comparerVersions(a, b) {
  const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) { const d = (pa[i] || 0) - (pb[i] || 0); if (d !== 0) return d < 0 ? -1 : 1; }
  return 0;
}

/**
 * « À propos de l'application » — s'ouvre sur l'événement window `ndoe:apropos`
 * (déclenché depuis le menu sidebar ET la navbar livreur). Montré une seule fois,
 * globalement (à côté de MajApp). Affiche :
 *  - la VERSION installée (savoir quelle version on a) ;
 *  - un bouton « Chercher les mises à jour » (contrôle MANUEL en plus de la
 *    vérification automatique de MajApp — règle de l'art) ;
 *  - le crédit « powered by LOGinSMart ».
 * Le FORÇAGE de mise à jour reste géré par MajApp via `app_min_version` (blocage
 * seulement quand une version est trop ancienne pour fonctionner) — pas ici.
 */
export default function AProposApp() {
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [natif, setNatif] = useState(false);
  const [checking, setChecking] = useState(false);
  const [statut, setStatut] = useState(null); // null | 'a-jour' | 'web' | 'erreur' | { derniere, url, notes }

  const chargerVersion = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      setNatif(true);
      try { const i = await CapApp.getInfo(); setVersion(i?.version || '—'); } catch { setVersion('—'); }
    } else { setNatif(false); setVersion('web'); }
  }, []);

  useEffect(() => {
    const h = () => { setStatut(null); setOpen(true); chargerVersion(); };
    window.addEventListener('ndoe:apropos', h);
    return () => window.removeEventListener('ndoe:apropos', h);
  }, [chargerVersion]);

  const chercher = async () => {
    setChecking(true); setStatut(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/app/version`, { timeout: 8000 });
      if (!Capacitor.isNativePlatform()) { setStatut('web'); return; }
      const cur = (await CapApp.getInfo())?.version || '0.0.0';
      if (data && data.derniere && comparerVersions(cur, data.derniere) < 0) {
        setStatut({ derniere: data.derniere, url: data.url, notes: data.notes });
      } else { setStatut('a-jour'); }
    } catch (e) { setStatut('erreur'); } finally { setChecking(false); }
  };

  const installer = (url) => {
    if (!url) return;
    const u = /^https?:\/\//i.test(url) ? url : `${DOMAIN}${url}`;
    window.open(u, '_system'); // le navigateur télécharge l'APK, l'utilisateur l'installe par-dessus
  };

  return (
    <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={380} centered title={null} className="apropos-modal">
      <div className="apropos">
        {/* Le logo de l'enseigne, pas un emoji de chaussure : c'est la seule
            fenêtre où l'application se présente, autant qu'elle le fasse sous
            son nom. Le logo porte déjà « Ndoé Boutique » et la signature
            « Class & Smart » — une ligne de texte en dessous ferait doublon,
            elle est donc retirée. Le nom reste porté par `alt`, pour les
            lecteurs d'écran et si l'image ne charge pas.
            PUBLIC_URL : chemin correct aussi bien sur le web que dans la
            WebView Android, où le bundle est servi depuis sa propre racine. */}
        <img
          className="apropos-logo"
          src={`${process.env.PUBLIC_URL}/logo.jpg`}
          alt="Ndoé Boutique"
        />
        <div className="apropos-version">Version {version}{!natif && <span className="apropos-web"> (navigateur)</span>}</div>

        <Button className="apropos-check" icon={<ReloadOutlined />} loading={checking} onClick={chercher}>
          Chercher les mises à jour
        </Button>

        {statut === 'a-jour' && <p className="apropos-ok"><CheckCircleOutlined /> Votre application est à jour.</p>}
        {statut === 'web' && <p className="apropos-ok"><CheckCircleOutlined /> Version web — toujours à jour.</p>}
        {statut === 'erreur' && <p className="apropos-err">Vérification impossible pour l’instant (réseau ?).</p>}
        {statut && typeof statut === 'object' && (
          <div className="apropos-maj">
            <p>Nouvelle version <b>{statut.derniere}</b> disponible.</p>
            {statut.notes && <p className="apropos-notes">{statut.notes}</p>}
            <Button type="primary" className="apropos-install" onClick={() => installer(statut.url)}>Mettre à jour maintenant</Button>
            <p className="apropos-aide">Le téléchargement s’ouvre dans le navigateur. Ouvrez le fichier pour installer par-dessus — rien n’est perdu.</p>
          </div>
        )}

        <div className="apropos-credit">powered by LOGinSMart</div>
      </div>
    </Modal>
  );
}
